import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useViewOrderContext } from "../../../contexts/ViewOrderContext";
import ToolTip from "../../../components/component-tools/ToolTip";
import { formatDate } from "../../../services/helpers";
import { deleteSubscriptionOrder } from "../../../services/subscriptionOrderService";
import ConfirmDelete from "../../../components/component-tools/ConfirmDelete";

interface SubscriptionOrderSectionProps {
  handleInputChange: (e: any, field: string, type?: string) => void;
  getServiceNames: (type: number) => string[];
}

const SubscriptionOrderSection: React.FC<SubscriptionOrderSectionProps> = ({
  handleInputChange,
  getServiceNames,
}) => {
  const navigate = useNavigate();
  const {
    order,
    subscriptionOrder,
    setSubscriptionOrder,
    subscriptionDetails,
    setSubscriptionDetails,
    services,
    vendors,
  } = useViewOrderContext();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [loadingSpinner, setLoadingSpinner] = useState(false);

  if (!subscriptionOrder || subscriptionOrder.subscriptionOrderID === 0)
    return null;

  const handleDelete = async () => {
    setConfirmDeleteOpen(false);
    setLoadingSpinner(true);

    try {
      await deleteSubscriptionOrder(subscriptionOrder.subscriptionOrderID);
      setSubscriptionOrder(undefined);
      setSubscriptionDetails([]);
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="data-container">
      <div className="text-container-header" style={{ flex: "0 0 30%" }}>
        Subscription Services
      </div>
      <div
        style={{
          flex: "0 0 65%",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", gap: "20px" }}>
          <div className="field-long" style={{ flex: "0 0 20%" }}>
            <button
              className={`oxbtn btn-new ${
                order.orderStatusID === 5 ? "disabled" : ""
              }`}
              disabled={order.orderStatusID === 5}
              onClick={() => {
                localStorage.setItem("scrollY", window.scrollY.toString());
                navigate("/admin/subscription-detail", {
                  state: {
                    subscriptionOrderID: subscriptionOrder?.subscriptionOrderID,
                  },
                });
              }}
            >
              <div>Order Service</div>
              <i className="bi bi-plus-circle-fill plus-icon"></i>
            </button>
          </div>
          <div className="field-long" style={{ flex: "0 0 20%" }}></div>
          <div className="checkbox-long">
            <div className="field-input" style={{ flex: "0 0 3%" }}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={
                  subscriptionOrder.adminReviewed
                    ? subscriptionOrder.adminReviewed
                    : false
                }
                onChange={(e) =>
                  handleInputChange(e, "adminReviewed", "subscription")
                }
                disabled={order.orderStatusID === 5}
              />
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <label className="field-label">Admin Reviewed </label>
              <ToolTip
                tooltipText="Please check this box if admin has completed all action items on this order. i.e. entered all vendor orders."
                placement="top"
                className="info-icon-spec"
              ></ToolTip>
            </div>
            <div></div>
          </div>
          <div className="checkbox-long">
            <div className="field-input" style={{ flex: "0 0 3%" }}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={
                  subscriptionOrder.inAdminReview
                    ? subscriptionOrder.inAdminReview
                    : false
                }
                onChange={(e) =>
                  handleInputChange(e, "inAdminReview", "subscription")
                }
                disabled={order.orderStatusID === 5}
              />
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <label className="field-label">In Review</label>
              <ToolTip
                tooltipText="Please check this box if an admin is currently working on this piece of the order."
                placement="top"
                className="info-icon-spec"
              ></ToolTip>
            </div>
            <div></div>
          </div>
        </div>
        {confirmDeleteOpen && (
          <ConfirmDelete
            onConfirm={() => handleDelete()}
            onCancel={() => {
              setConfirmDeleteOpen(false);
            }}
            message="Delete Subscription Order? All details will be deleted."
          />
        )}
        {!subscriptionDetails.some((cd) => cd.initiatedDate) && (
          <div className="close-button-container">
            <button
              className="delete-button"
              onClick={() => setConfirmDeleteOpen(true)}
            >
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}
      </div>
      <div style={{ flex: "0 0 100%" }}>
        <ul>
          {getServiceNames(3).map((serviceName, index) => (
            <li key={index}>{serviceName}</li>
          ))}
        </ul>
      </div>

      <div className="table-wrapper">
        <div className="table-container">
          <table className="table" style={{ cursor: "pointer" }}>
            <thead>
              <tr>
                <th style={{ width: "180px" }}>Service</th>
                <th style={{ width: "130px" }}></th>
                <th style={{ width: "130px" }}></th>
                <th
                  style={{
                    width: "120px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "5px",
                    }}
                  >
                    <div>Initiated</div>
                  </div>
                </th>
                <th style={{ width: "120px" }}>End</th>
                <th style={{ width: "150px" }}>Vendor</th>
                <th style={{ width: "80px" }}>Fee</th>
                <th style={{ width: "80px" }}>Inactive</th>
              </tr>
            </thead>
            <tbody>
              {subscriptionDetails?.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center" }}>
                    No subscription details found.
                  </td>
                </tr>
              ) : (
                subscriptionDetails?.map((detail) => {
                  const service = services.find(
                    (s) => s.serviceID === detail.serviceID,
                  );
                  return (
                    <tr
                      key={detail.subscriptionDetailID}
                      onClick={() => {
                        localStorage.setItem(
                          "scrollY",
                          window.scrollY.toString(),
                        );
                        navigate(
                          `/admin/subscription-detail/${detail.subscriptionDetailID}`,
                          {
                            state: {
                              subscriptionOrderID:
                                subscriptionOrder.subscriptionOrderID,
                              subscriptionDetailID: detail.subscriptionDetailID,
                            },
                          },
                        );
                      }}
                    >
                      <td>
                        {service
                          ? service.serviceName
                          : `Unknown Service (ID: ${detail.serviceID})`}
                      </td>
                      <td>{detail.resultsFieldOne}</td>
                      <td>{detail.resultsFieldTwo}</td>
                      <td>{formatDate(detail.initiatedDate, "us")}</td>
                      <td>{formatDate(detail.stopDate, "us")}</td>
                      <td>
                        {vendors.find(
                          (vendor) => vendor.vendorID === detail.vendorID,
                        )?.vendorName || ``}
                      </td>
                      <td>{detail.fee}</td>
                      <td
                        style={{
                          fontWeight: detail.inActive ? "bold" : "normal",
                        }}
                      >
                        {detail.inActive ? "Inactive" : ""}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionOrderSection;
